/**
 * The mode configuration contract. `docs/DESIGN.md`'s owner amendment (the
 * "Mode configuration" table): a goal axis (sprint only) and a hardness axis
 * (both solo modes), chosen on the mode-select screen and shown in the HUD.
 * `docs/decisions/0021` is the ADR for the shape below.
 *
 * `ModeConfig` is deliberately plain, JSON-serialisable data — every field is
 * a primitive or a literal union, nothing here is a function or holds a
 * reference to engine state — because it is meant to travel: today it goes
 * from the mode-select screen into `buildMode` (`registry.ts`) and into the
 * personal-bests store's key; a duel room in a later phase can put the same
 * shape on the wire (`packages/protocol`) with no translation layer, since
 * there is nothing in it a server cannot also construct and validate.
 */
import { msToTicks } from '@interlock/engine';

import type { ModeId } from './types.js';

export type Hardness = 'easy' | 'normal' | 'hard' | 'expert';

/** Registry order, easiest first — also the order the HUD and mode-select present them in. */
export const HARDNESS_IDS: readonly Hardness[] = ['easy', 'normal', 'hard', 'expert'];

export const DEFAULT_HARDNESS: Hardness = 'normal';

/**
 * `docs/DESIGN.md`: "Hardness... gravity curve × 0.7/1.0/1.5/2.2", written
 * in thousandths because the engine's gravity is integer arithmetic
 * (`GravityConfig.speedPermille`, `docs/decisions/0050`): 700 is 0.7x. The
 * one number every hardness level changes, for every mode, solo and versus.
 */
export const HARDNESS_GRAVITY_PERMILLE: Readonly<Record<Hardness, number>> = {
  easy: 700,
  normal: 1000,
  hard: 1500,
  expert: 2200,
};

/** `docs/DESIGN.md`: "Survival row cadence 10s/8s(default)/6s/4s." Survival's own axis only. */
export const HARDNESS_SURVIVAL_INTERVAL_MS: Readonly<Record<Hardness, number>> = {
  easy: 10_000,
  normal: 8_000,
  hard: 6_000,
  expert: 4_000,
};

/**
 * The survival cadence in whole ticks: 600, 480, 360 and 240. The conversion
 * is exact for all four, and this is the one place it is made — solo
 * survival, `duelSurvival` and `coopWide` all hand this number to the same
 * rising-floor rule (`@interlock/sim`).
 */
export function survivalFloorTicks(hardness: Hardness): number {
  return Math.max(1, msToTicks(HARDNESS_SURVIVAL_INTERVAL_MS[hardness]));
}

const HARDNESS_LABEL: Readonly<Record<Hardness, string>> = {
  easy: 'Easy',
  normal: 'Normal',
  hard: 'Hard',
  expert: 'Expert',
};

export function describeHardness(hardness: Hardness): string {
  return HARDNESS_LABEL[hardness];
}

/**
 * Sprint's goal axis. `docs/DESIGN.md`: "lines target 10/20/40(default)/100 —
 * OR a time limit 1/2/3/5 min (timed variant: clear as many lines as
 * possible; ending is always 'finished', result = lines)." The two shapes
 * are mutually exclusive, not a lines-target-with-an-optional-cap — picking
 * "time" turns off the lines target entirely, per the spec's "OR".
 */
export type SprintGoal =
  | { readonly kind: 'lines'; readonly lines: number }
  | { readonly kind: 'time'; readonly ms: number };

export const SPRINT_LINE_GOALS: readonly number[] = [10, 20, 40, 100];
export const SPRINT_DEFAULT_LINE_GOAL = 40;

/** Whole minutes, `docs/DESIGN.md`'s own units — converted to ms only where the engine needs it. */
export const SPRINT_TIME_GOALS_MIN: readonly number[] = [1, 2, 3, 5];

export function describeGoal(goal: SprintGoal | undefined): string | null {
  if (goal === undefined) return null;
  return goal.kind === 'lines' ? `${String(goal.lines)} lines` : `${String(goal.ms / 60_000)} min`;
}

/**
 * The whole configuration a solo run — and, later, a duel room reusing this
 * type — is built from. `modeId` names which mode, `hardness` applies to
 * every mode, `goal` is sprint's own axis and is meaningless (and ignored)
 * for survival, which has no goal to choose.
 */
export interface ModeConfig {
  readonly modeId: ModeId;
  readonly hardness: Hardness;
  readonly goal?: SprintGoal;
}

/** One line summarising the chosen configuration, for the HUD and the results screen. */
export function describeModeConfig(config: ModeConfig): string {
  const parts = [describeHardness(config.hardness)];
  const goal = describeGoal(config.goal);
  if (goal !== null) parts.push(goal);
  return parts.join(' · ');
}

/**
 * A stable string key for the personal-bests store — `docs/DESIGN.md`: "keep
 * local personal bests in localStorage keyed by mode id + config." Plain
 * string concatenation rather than `JSON.stringify`: the field order in a
 * serialised object is not guaranteed stable across engines, and this key is
 * meant to be read back byte-for-byte on every future visit.
 */
export function encodeModeConfig(config: ModeConfig): string {
  const goal = config.goal
    ? `:${config.goal.kind}-${String(config.goal.kind === 'lines' ? config.goal.lines : config.goal.ms)}`
    : '';
  return `${config.modeId}:${config.hardness}${goal}`;
}

export function defaultSprintConfig(): ModeConfig {
  return {
    modeId: 'sprint',
    hardness: DEFAULT_HARDNESS,
    goal: { kind: 'lines', lines: SPRINT_DEFAULT_LINE_GOAL },
  };
}

export function defaultSurvivalConfig(): ModeConfig {
  return { modeId: 'survival', hardness: DEFAULT_HARDNESS };
}

/** The default configuration for a known mode id. Throws for anything else — see `registry.ts#buildMode`. */
export function defaultConfigFor(id: ModeId): ModeConfig {
  switch (id) {
    case 'sprint':
      return defaultSprintConfig();
    case 'survival':
      return defaultSurvivalConfig();
    default:
      throw new Error(`unknown mode id: ${id}`);
  }
}
